/**
 * RedoReasonDialog — shared two-step redo confirmation dialog (production stations).
 *
 * Extracted from catcher-station (2026-07-21) when the DTF film-print station
 * became the second consumer. All copy is injected via `copy` so each station
 * frames the redo in its own language — the catcher scraps + recuts a fresh
 * garment; the film-print station scraps + re-sends a DTF film (the garment is
 * untouched). Reasons are injected too (per-org configurable for the catcher; a
 * fixed film list for the film station).
 *
 * Behaviour preserved verbatim from the catcher original:
 * - Phase 1: reason grid (no mutation fires). Phase 2: confirmation view.
 * - Scanner-proof: no autoFocus on open or on any button; Back focused on the
 *   phase transition so an in-flight scanner Enter can't fire the mutation.
 * - Keyboard hotkeys: 1-9 select reason, Enter confirms, Escape closes.
 * - Large touch targets (h-16 / h-14) for gloved operators.
 */
import { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { AlertTriangle, Frown, Loader2 } from 'lucide-react';
import { useBranding } from '@/hooks/useBranding';

export type RedoReason = string;

export interface RedoReasonOption {
  value: string;
  label: string;
  description: string;
}

/** Station-specific copy — resolved by the caller so this component carries no
 * i18n namespace coupling. */
export interface RedoReasonDialogCopy {
  /** Phase 1 title — e.g. "Select redo reason" / "Why redo this film?". */
  selectTitle: string;
  /** Phase 2 title — e.g. "Confirm redo" / "Redo this film?". */
  confirmTitle: string;
  /** Phase 2 confirm button label. */
  confirmButtonLabel: string;
  /** Shown while the mutation is pending — e.g. "Creating redo…" / "Scrapping film…". */
  pendingLabel: string;
  /** Optional explanatory line under the reason card on the confirm phase. */
  bodyText?: string;
  /** Shown while the reason list is loading (defaults to `pendingLabel`). */
  loadingReasonsLabel?: string;
  /** aria-label for the confirm button (receives the chosen reason's label). */
  confirmAriaLabel?: (reasonLabel: string) => string;
  /** aria-label for the Back button. */
  goBackAriaLabel?: string;
}

interface RedoReasonDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (reason: RedoReason) => void;
  isPending: boolean;
  reasons: RedoReasonOption[];
  reasonsLoading?: boolean;
  copy: RedoReasonDialogCopy;
}

export function RedoReasonDialog({
  open,
  onOpenChange,
  onConfirm,
  isPending,
  reasons,
  reasonsLoading,
  copy,
}: RedoReasonDialogProps) {
  const { t } = useTranslation('production');
  const branding = useBranding();
  const [selectedReason, setSelectedReason] = useState<string | null>(null);
  const backButtonRef = useRef<HTMLButtonElement>(null);

  // Focus Back button when transitioning to confirmation phase (scanner-proofing).
  // If Radix focus trap moves focus after a reason button unmounts, landing on
  // Confirm + an in-flight scanner Enter could fire the mutation. Focusing Back
  // means worst case = returning to the reason grid.
  useEffect(() => {
    if (selectedReason && backButtonRef.current) {
      backButtonRef.current.focus();
    }
  }, [selectedReason]);

  // Reset selectedReason when dialog closes
  const handleOpenChange = useCallback(
    (isOpen: boolean) => {
      if (!isOpen) {
        setSelectedReason(null);
      }
      onOpenChange(isOpen);
    },
    [onOpenChange],
  );

  // Prevent auto-focus on dialog open (scanner-proofing)
  const handleOpenAutoFocus = useCallback((e: Event) => {
    e.preventDefault();
  }, []);

  // Keyboard hotkeys for the dialog
  // Phase 1: 1-9 selects reason
  // Phase 2: Enter confirms (Escape closes dialog)
  useEffect(() => {
    if (!open || isPending) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.altKey || e.metaKey) return;

      if (!selectedReason) {
        // Phase 1: number keys select reason
        const num = parseInt(e.key, 10);
        const reason = num >= 1 && num <= reasons.length ? reasons[num - 1] : undefined;
        if (reason) {
          e.preventDefault();
          e.stopPropagation();
          setSelectedReason(reason.value);
        }
      } else {
        // Phase 2: Enter confirms
        if (e.key === 'Enter') {
          e.preventDefault();
          e.stopPropagation();
          onConfirm(selectedReason);
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [open, isPending, selectedReason, reasons, onConfirm]);

  const selectedReasonData = selectedReason
    ? reasons.find((r) => r.value === selectedReason)
    : null;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="sm:max-w-lg"
        onOpenAutoFocus={handleOpenAutoFocus}
      >
        <DialogHeader>
          <DialogTitle className="text-xl flex items-center gap-2.5">
            {branding.mascotUrl ? (
              <img
                src={branding.mascotUrl}
                alt=""
                className="h-14 w-14 shrink-0"
                aria-hidden="true"
                onError={(e) => {
                  (e.currentTarget as HTMLImageElement).style.display = 'none';
                }}
              />
            ) : (
              <Frown className="h-14 w-14 shrink-0 text-muted-foreground" aria-hidden="true" />
            )}
            {selectedReason ? copy.confirmTitle : copy.selectTitle}
          </DialogTitle>
        </DialogHeader>

        {isPending ? (
          <div className="flex flex-col items-center justify-center py-8 min-h-[280px]">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground mb-4" />
            <p className="text-muted-foreground">{copy.pendingLabel}</p>
          </div>
        ) : reasonsLoading ? (
          <div className="flex flex-col items-center justify-center py-8 min-h-[280px]">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground mb-4" />
            <p className="text-muted-foreground">{copy.loadingReasonsLabel ?? copy.pendingLabel}</p>
          </div>
        ) : selectedReason && selectedReasonData ? (
          /* Phase 2: Confirmation view */
          <div className="flex flex-col gap-4 py-4 min-h-[280px]">
            {/* Selected reason card with warning */}
            <div className="rounded-lg border border-destructive/30 bg-destructive/10 p-4 flex items-start gap-3">
              <AlertTriangle className="h-6 w-6 text-destructive shrink-0 mt-0.5" />
              <div>
                <p className="text-lg font-semibold">{selectedReasonData.label}</p>
                <p className="text-sm text-muted-foreground">
                  {selectedReasonData.description}
                </p>
              </div>
            </div>

            {/* Optional station-specific explanatory line. */}
            {copy.bodyText && (
              <p className="text-sm text-muted-foreground">{copy.bodyText}</p>
            )}

            {/* Confirm button - destructive, large touch target */}
            <Button
              variant="destructive"
              className="w-full h-16 text-xl font-semibold"
              onClick={() => onConfirm(selectedReason)}
              disabled={isPending}
              aria-label={copy.confirmAriaLabel?.(selectedReasonData.label) ?? copy.confirmButtonLabel}
            >
              {copy.confirmButtonLabel}
              <kbd className="ml-2 text-xs font-normal opacity-60 bg-white/20 px-1.5 py-0.5 rounded">Enter</kbd>
            </Button>

            {/* Back button - outline for visible touch boundary (gloved workers) */}
            <Button
              ref={backButtonRef}
              variant="outline"
              className="w-full h-14 text-lg"
              onClick={() => setSelectedReason(null)}
              disabled={isPending}
              aria-label={copy.goBackAriaLabel}
            >
              {t('common:back')}
            </Button>

            {/* Cancel button - closes dialog entirely */}
            <Button
              variant="ghost"
              className="w-full h-14"
              onClick={() => handleOpenChange(false)}
              disabled={isPending}
            >
              {t('common:cancel')}
            </Button>
          </div>
        ) : (
          /* Phase 1: Reason selection grid */
          <div className="flex flex-col">
            <div className="max-h-[60vh] overflow-y-auto py-4">
              <div className="grid grid-cols-2 gap-3">
                {reasons.map((reason, index) => (
                  <Button
                    key={reason.value}
                    variant="outline"
                    className="h-auto min-h-[5rem] flex flex-col items-center justify-center text-center p-3 whitespace-normal hover:border-destructive hover:bg-destructive/5 relative"
                    onClick={() => setSelectedReason(reason.value)}
                  >
                    <kbd className="absolute top-2 left-2 text-[10px] font-normal text-muted-foreground border bg-muted px-1.5 py-0.5 rounded min-w-[20px] text-center">
                      {index + 1}
                    </kbd>
                    <span className="font-semibold text-base w-full">
                      {reason.label}
                    </span>
                    {reason.description && (
                      <span className="text-xs text-muted-foreground mt-1 leading-tight w-full">
                        {reason.description}
                      </span>
                    )}
                  </Button>
                ))}
              </div>
            </div>

            <Button
              variant="ghost"
              className="w-full h-14 shrink-0"
              onClick={() => handleOpenChange(false)}
            >
              {t('common:cancel')}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
